import { useEffect, useMemo, useState } from 'react';
import { useQueries, useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ArrowRight, BookOpen, Check, Copy, FileText, ImageIcon, Search, Users } from 'lucide-react';
import { api } from '../api';
import type { OpeningBalance, Party, PartyBalance } from '../api';
import { AppShell } from '../ui/AppShell';
import { PageHeader } from '../ui/PageHeader';
import { ActionQueueCard, MetricStrip } from '../ui/mobileOps';
import { Btn, EmptyState, RowSkeleton, SectionCard, StatePill } from '../ui/components';
import { Button, Checkbox, TextInput } from '../ui/primitives';
import { buildShareCaption } from '../ledger/shareCaption';
import { withCacheBust } from '../ledger/exportUrl';

const SHOW_DL_KEY = 'ledgers.showDownloadButtons';

type PartyStatusFilter = 'active' | 'archived' | 'all';

export function LedgersIndexPage() {
  const [statusFilter, setStatusFilter] = useState<PartyStatusFilter>('active');
  const [filter, setFilter] = useState('');
  const [showDownloads, setShowDownloads] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(SHOW_DL_KEY) === 'true';
  });
  const [selected, setSelected] = useState<Record<number, boolean>>({});
  const [bulkBusy, setBulkBusy] = useState(false);

  const partiesQ = useQuery<Party[]>({
    queryKey: ['parties', statusFilter],
    queryFn: () =>
      api.get<Party[]>(`/api/parties${statusFilter === 'all' ? '' : `?status=${statusFilter}`}`),
  });

  const parties = useMemo(
    () => (partiesQ.data ?? []).filter((party) => party.role !== 'self'),
    [partiesQ.data],
  );

  const today = useMemo(() => todayIso(), []);

  const balanceQueries = useQueries({
    queries: parties.map((party) => ({
      queryKey: ['balance', party.id],
      queryFn: () => api.get<PartyBalance>(`/api/parties/${party.id}/balance`),
    })),
  });

  const openingQueries = useQueries({
    queries: parties.map((party) => ({
      queryKey: ['opening-balance', party.id, today],
      queryFn: () =>
        api.get<OpeningBalance | null>(`/api/opening-balances/${party.id}?asOf=${today}`).catch(() => null),
    })),
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(SHOW_DL_KEY, showDownloads ? 'true' : 'false');
  }, [showDownloads]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const joined = parties.map((party, index) => ({
      party,
      balance: balanceQueries[index]?.data?.balance ?? '…',
      opening: openingQueries[index]?.data ?? null,
    }));
    const list = q.length === 0 ? joined : joined.filter((item) => item.party.displayName.toLowerCase().includes(q));
    return list.sort((left, right) => {
      const a = Math.abs(Number(left.balance === '…' ? '0' : left.balance));
      const b = Math.abs(Number(right.balance === '…' ? '0' : right.balance));
      return b - a;
    });
  }, [balanceQueries, filter, openingQueries, parties]);

  const rangedItems = useMemo(
    () =>
      filtered.map((item) => ({
        ...item,
        from: item.opening ? addDaysIso(item.opening.asOfDate, 1) : addDaysIso(today, -365),
        to: today,
      })),
    [filtered, today],
  );
  const selectedItems = rangedItems.filter((it) => selected[it.party.id]);
  const allSelected = rangedItems.length > 0 && rangedItems.every((it) => selected[it.party.id]);

  function toggleAll() {
    const next = !allSelected;
    const m: Record<number, boolean> = {};
    for (const it of rangedItems) m[it.party.id] = next;
    setSelected(m);
  }

  async function downloadBulk(type: 'pdf' | 'png') {
    if (selectedItems.length === 0) return;
    setBulkBusy(true);
    try {
      for (const it of selectedItems) {
        const url = withCacheBust(`/api/parties/${it.party.id}/ledger.${type}?from=${it.from}&to=${it.to}`);
        const a = document.createElement('a');
        a.href = url;
        a.download = `ledger-${slugify(it.party.displayName) || `party-${it.party.id}`}-${it.to}.${type}`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        await new Promise((r) => setTimeout(r, 450));
      }
    } finally {
      setBulkBusy(false);
    }
  }

  const kpis = useMemo(() => {
    let net = 0;
    let uplineDebt = 0;
    let clientReceivables = 0;
    parties.forEach((party, index) => {
      const raw = balanceQueries[index]?.data?.balance;
      if (!raw || raw === '…') return;
      const value = Number(raw);
      if (!Number.isFinite(value)) return;
      net += value;
      if (party.role === 'upline') uplineDebt += value;
      if (party.role === 'client') clientReceivables += value;
    });
    return { net, uplineDebt, clientReceivables };
  }, [balanceQueries, parties]);

  return (
    <AppShell
      title="Ledgers"
      toolbar={<PageHeader section="ledgers" title="Ledgers" subtitle="Party balances and quick statement access" />}
    >
      <div className="flex flex-col gap-4">
        {/* Explicit order on every child: mobile = intro → find/filter → list →
            KPIs; desktop = KPIs → intro → find/filter → list. Without an order
            class the list card defaults to order 0 and jumps ABOVE the search
            box it's filtered by. */}
        <div className="order-4 md:order-1">
          {!partiesQ.isLoading && (
            <MetricStrip
            section="ledgers"
            items={[
              {
                label: 'Net position',
                value: kpis.net.toFixed(3),
                note: 'all party balances',
                tone: toneSign(kpis.net),
              },
              {
                label: 'Upline debt',
                value: kpis.uplineDebt.toFixed(3),
                note: 'you owe uplines',
                tone: toneSign(-kpis.uplineDebt),
              },
              {
                label: 'Receivables',
                value: kpis.clientReceivables.toFixed(3),
                note: 'clients owe you',
                tone: toneSign(kpis.clientReceivables),
              },
            ]}
            />
          )}
        </div>

        <div className="order-1 md:order-2">
          <ActionQueueCard
          section="ledgers"
          eyebrow="Find a ledger"
          title="Find the ledger you need"
          description="Search, filter, and jump straight into the running balance."
          tone="neutral"
        />

        </div>

        <div className="order-2 md:order-3">
          <SectionCard section="ledgers" title="Find and filter">
          <div className="space-y-3">
            <TextInput
              placeholder="Search parties"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              name="ledger-search"
            />
            <div className="flex flex-wrap items-center gap-2">
              {([
                { value: 'active', label: 'Active' },
                { value: 'archived', label: 'Archived' },
                { value: 'all', label: 'All' },
              ] as const).map((option) => {
                const active = statusFilter === option.value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setStatusFilter(option.value)}
                    className="rounded-full border px-3 py-1.5 text-small font-medium transition-colors"
                    style={
                      active
                        ? { background: 'var(--sec-ledgers)', color: '#fff', borderColor: 'var(--sec-ledgers)' }
                        : { background: '#fff', color: 'var(--ink-muted)', borderColor: 'var(--border)' }
                    }
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
            <Checkbox
              label="Show download buttons"
              checked={showDownloads}
              onChange={setShowDownloads}
            />
          </div>
        </SectionCard>

        </div>

        <SectionCard
          className="order-3 md:order-4"
          section="ledgers"
          title="Party ledgers"
          action={
            <span className="text-caption text-ink-muted">
              {selectedItems.length > 0 ? `${selectedItems.length} selected` : `${filtered.length} shown`}
            </span>
          }
        >
          {partiesQ.isLoading ? (
            <RowSkeleton rows={6} />
          ) : parties.length === 0 ? (
            <EmptyState
              section="ledgers"
              icon={Users}
              title="No parties yet"
              description="Add clients and your uplines to start tracking balances."
              action={
                <Btn size="sm" onClick={() => window.location.assign('/wizard')}>
                  Go to setup
                  <ArrowRight className="h-4 w-4" strokeWidth={2} />
                </Btn>
              }
            />
          ) : filtered.length === 0 ? (
            <EmptyState
              section="ledgers"
              icon={Search}
              title="No matching party"
              description="Try a shorter search or switch the active / archived filter."
            />
          ) : (
            <>
              {/* Bulk select + download bar */}
              <div className="mb-3 flex flex-wrap items-center gap-2 rounded-2xl border border-border bg-surface-raised px-3 py-2">
                <button
                  type="button"
                  onClick={toggleAll}
                  className="group inline-flex min-h-[44px] items-center gap-2.5 rounded-xl px-2.5 py-1.5 text-small font-semibold text-ink transition-colors hover:bg-surface"
                >
                  <SelectBox checked={allSelected} />
                  {allSelected ? 'Clear all' : 'Select all'}
                </button>
                <span className="text-caption text-ink-muted">
                  {selectedItems.length > 0 ? `${selectedItems.length} selected` : 'none selected'}
                </span>
                <div className="ml-auto flex items-center gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={selectedItems.length === 0 || bulkBusy}
                    onClick={() => void downloadBulk('png')}
                  >
                    <ImageIcon className="h-3.5 w-3.5" strokeWidth={2} /> PNG
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={selectedItems.length === 0 || bulkBusy}
                    onClick={() => void downloadBulk('pdf')}
                  >
                    <FileText className="h-3.5 w-3.5" strokeWidth={2} /> PDF
                  </Button>
                </div>
              </div>
              <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                {rangedItems.map((item) => (
                  <LedgerPartyTicket
                    key={item.party.id}
                    party={item.party}
                    balance={item.balance}
                    from={item.from}
                    to={item.to}
                    showDownloads={showDownloads}
                    selected={!!selected[item.party.id]}
                    onToggle={() =>
                      setSelected((m) => ({ ...m, [item.party.id]: !m[item.party.id] }))
                    }
                  />
                ))}
              </div>
            </>
          )}
        </SectionCard>
      </div>
    </AppShell>
  );
}

/**
 * The visual box for select-all / per-card selection. Deliberately larger and
 * higher-contrast than a native checkbox: 22px, 2px border (muted ink when
 * idle — not the near-invisible hairline border token), section-emerald fill
 * when checked. Purely visual — the wrapping <button> owns the click and a11y.
 */
function SelectBox({ checked }: { checked: boolean }) {
  return (
    <span
      aria-hidden="true"
      className={`flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-[7px] border-2 transition-all duration-150 ${
        checked
          ? 'border-sec-ledgers bg-sec-ledgers text-white'
          : 'border-ink-muted bg-white group-hover:border-sec-ledgers'
      }`}
    >
      <Check
        className={`h-3.5 w-3.5 transition-transform duration-150 ${checked ? 'scale-100' : 'scale-0'}`}
        strokeWidth={3.5}
      />
    </span>
  );
}

function LedgerPartyTicket({
  party,
  balance,
  from,
  to,
  showDownloads,
  selected,
  onToggle,
}: {
  party: Party;
  balance: string;
  from: string;
  to: string;
  showDownloads: boolean;
  selected: boolean;
  onToggle: () => void;
}) {
  const signText =
    balance === '…' || balance === '0' ? '' : balance.startsWith('-') ? 'you owe' : 'owes you';
  const [copied, setCopied] = useState(false);
  const balanceReady = balance !== '…';

  function copyCaption() {
    if (!balanceReady) return;
    void navigator.clipboard.writeText(buildShareCaption(balance)).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    });
  }

  return (
    <div
      className={`rounded-[22px] border bg-surface p-4 shadow-card transition-colors ${
        selected ? 'border-sec-ledgers ring-1 ring-sec-ledgers/40' : 'border-border'
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-2.5">
          {/* 22px visual box inside a ≥44px tap target (padding + negative
              margin keeps the layout tight while the hit area stays large). */}
          <button
            type="button"
            onClick={onToggle}
            aria-label={selected ? 'Deselect' : 'Select'}
            aria-pressed={selected}
            className="group -m-[11px] shrink-0 rounded-xl p-[11px] transition-colors hover:bg-surface-raised"
          >
            <SelectBox checked={selected} />
          </button>
          <div className="min-w-0">
            <div className="truncate text-h1 font-semibold text-ink">{party.displayName}</div>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <StatePill state={party.role} />
              {party.status === 'archived' && <StatePill state="archived" />}
            </div>
          </div>
        </div>
        <Link
          to={`/parties/${party.id}/ledger`}
          className="inline-flex shrink-0 items-center gap-1 rounded-full border border-border bg-white px-3 py-1.5 text-small font-medium text-sec-ledgers transition-colors hover:bg-surface-raised"
        >
          Open
          <ArrowRight className="h-3.5 w-3.5" strokeWidth={2} />
        </Link>
      </div>

      <div className="mt-4 flex items-end justify-between gap-3">
        <div className="min-w-0">
          <div className={`font-mono text-display font-bold tabular-nums ${toneFromBalance(balance)}`}>
            {balance}
          </div>
          <div className="mt-1 text-caption uppercase tracking-[0.14em] text-ink-muted">
            {signText || 'settled'}
          </div>
        </div>
        <button
          type="button"
          onClick={copyCaption}
          disabled={!balanceReady}
          title="Copy WhatsApp caption"
          className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-border bg-white px-3 py-1.5 text-caption font-medium text-ink transition-colors hover:bg-surface-raised disabled:opacity-50"
        >
          {copied ? (
            <>
              <Check className="h-3.5 w-3.5 text-pos" strokeWidth={2.5} /> Copied
            </>
          ) : (
            <>
              <Copy className="h-3.5 w-3.5 text-ink-muted" strokeWidth={2} /> Caption
            </>
          )}
        </button>
      </div>

      {showDownloads && (
        <div className="mt-4 border-t border-border pt-4">
          <DownloadLinks partyId={party.id} partyName={party.displayName} from={from} to={to} />
        </div>
      )}
    </div>
  );
}

function DownloadLinks({
  partyId,
  partyName,
  from,
  to,
}: {
  partyId: number;
  partyName: string;
  from: string;
  to: string;
}) {
  const pdfHref = withCacheBust(`/api/parties/${partyId}/ledger.pdf?from=${from}&to=${to}`);
  const pngHref = withCacheBust(`/api/parties/${partyId}/ledger.png?from=${from}&to=${to}`);
  const slug = slugify(partyName) || `party-${partyId}`;

  return (
    <div className="flex items-center gap-2">
      <a
        href={pdfHref}
        target="_blank"
        rel="noopener"
        className="inline-flex items-center gap-1.5 rounded-full border border-border bg-white px-3 py-1.5 text-caption font-medium text-ink transition-colors hover:bg-surface-raised"
        title="Open PDF in a new tab"
      >
        <FileText className="h-3.5 w-3.5 text-ink-muted" strokeWidth={2} />
        PDF
      </a>
      <a
        href={pngHref}
        download={`ledger-${slug}-${from}-${to}.png`}
        className="inline-flex items-center gap-1.5 rounded-full border border-border bg-white px-3 py-1.5 text-caption font-medium text-ink transition-colors hover:bg-surface-raised"
        title="Download PNG"
      >
        <ImageIcon className="h-3.5 w-3.5 text-ink-muted" strokeWidth={2} />
        PNG
      </a>
    </div>
  );
}

function slugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function toneFromBalance(v: string): string {
  if (v === '…' || v === '0') return 'text-ink-muted';
  if (v.startsWith('-')) return 'text-neg';
  return 'text-pos';
}

function toneSign(value: number): 'pos' | 'neg' | 'default' {
  if (value < 0) return 'neg';
  if (value > 0) return 'pos';
  return 'default';
}

function todayIso(): string {
  const d = new Date();
  const y = d.getFullYear().toString().padStart(4, '0');
  const m = (d.getMonth() + 1).toString().padStart(2, '0');
  const day = d.getDate().toString().padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function addDaysIso(iso: string, delta: number): string {
  const [y, m, d] = iso.split('-').map((s) => parseInt(s, 10));
  const date = new Date(y!, (m ?? 1) - 1, d ?? 1);
  date.setDate(date.getDate() + delta);
  const yy = date.getFullYear().toString().padStart(4, '0');
  const mm = (date.getMonth() + 1).toString().padStart(2, '0');
  const dd = date.getDate().toString().padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
}
